import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import styles from '../BranchDrawer.module.css';
import Ico from '../../common/Ico';
import { ICO_EXTERNAL } from './constants';
import { apiGetNightlyChangelog, apiGetBranchChangelogStats, apiReanalyzeChangelog } from '../../../api/nightly';

/* Stats derived from the stored snapshot: entries/skips come from `components` —
   the API's payload is the source of truth the panel trusts. */
function statsLine(changelog) {
  const components = changelog.components || [];
  const inRange = components.filter((c) => c.source === 'this-run' && !c.skipped);
  const excluded = components.reduce((n, c) => n + ((c.excluded || []).length), 0);
  return `${changelog.entry_ids.length} changes · ${inRange.length} components · ${excluded} excluded`;
}

/* Grounded-vs-ungrounded trend across recent runs: the evidence feed for flipping the
   readiness check to required. Pure presentational bars — no chart dependency. */
function GroundedTrend({ stats, runId }) {
  if (!stats.length) return null;
  // A run older than the trend window has no stats row here; claiming the newest run's
  // percentage as "this run's" would lie, so the label degrades to the neutral form.
  const current = stats.find((s) => s.run_id === runId);
  const currentPct = current ? Math.round((current.grounded / Math.max(current.entries, 1)) * 100) : null;
  return (
    <div className={styles.changelogTrend}>
      {[...stats].reverse().map((s) => {
        const total = Math.max(s.entries, 1);
        const pct = Math.round((s.grounded / total) * 100);
        return (
          <div
            key={s.run_id}
            className={styles.trendCol}
            data-tip={`${s.run_id}: ${s.grounded}/${s.entries} issue-linked`}
          >
            <div className={styles.trendBar}>
              <div className={styles.trendFill} style={{ height: `${pct}%` }} />
            </div>
          </div>
        );
      })}
      <span className={styles.trendLabel}>
        {currentPct === null
          ? `issue-linked share · last ${stats.length} nightlies`
          : `${currentPct}% of this run's changes are linked to an issue · last ${stats.length} nightlies`}
      </span>
    </div>
  );
}

/* Links: commit URLs render as the same chip-with-external-icon language the SOURCE COMMITS
   cards use; everything else is a regular link. stopPropagation keeps the drawer from
   swallowing the click. */
export function MarkdownLink({ href, children, ...props }) {
  const isCommit = (href || '').includes('/commit/') || (href || '').includes('/compare/');
  const common = {
    href,
    target: '_blank',
    rel: 'noreferrer',
    onClick: (e) => e.stopPropagation(),
  };
  if (isCommit) {
    return (
      <a {...common} className={styles.commitChip} title={href}>
        <code className={styles.commitCardSha}>{children}</code>
        <Ico d={ICO_EXTERNAL} size={11} style={{ color: 'var(--fg-link)', flexShrink: 0 }} />
      </a>
    );
  }
  return <a {...common} {...props}>{children}</a>;
}

/* Every commit in range is accounted for: entry, exclusion, or skip. Exclusions get their
   own tab — off the main read, one click from the audit. Numeric keys are PRs, hex are
   commits; both link out. */
function excludedUrl(repo, changeKey) {
  return /^\d+$/.test(changeKey)
    ? `https://github.com/${repo}/pull/${changeKey}`
    : `https://github.com/${repo}/commit/${changeKey}`;
}

function MaintenanceTab({ components }) {
  const rows = components.flatMap((c) => (c.maintenance || []).map((m) => ({ repo: c.repo, ...m })));
  if (!rows.length) {
    return <div className={styles.empty}>No maintenance changes in this changelog.</div>;
  }
  return (
    <div className={styles.excludedList} data-testid="maintenance-tab">
      {rows.map((row) => (
        <div key={`${row.repo}:${row.change_key}`} className={styles.maintenanceRow}>
          <div className={styles.excludedRow}>
            <span className={styles.excludedRepo}>{row.repo.split('/').pop()}</span>
            {(row.backport_pr || row.main_pr) && (
              <a className={styles.commitChip} href={(row.backport_pr || row.main_pr).url}
                 target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                <code className={styles.commitCardSha}>#{(row.backport_pr || row.main_pr).number}</code>
                <Ico d={ICO_EXTERNAL} size={11} style={{ color: 'var(--fg-link)', flexShrink: 0 }} />
              </a>
            )}
            {row.commit && (
              <a className={styles.commitChip} href={`https://github.com/${row.repo}/commit/${row.commit}`}
                 target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                <code className={styles.commitCardSha}>{row.commit.slice(0, 7)}</code>
                <Ico d={ICO_EXTERNAL} size={11} style={{ color: 'var(--fg-link)', flexShrink: 0 }} />
              </a>
            )}
            {row.author && <span className={styles.excludedReason}>@{row.author}</span>}
          </div>
          <div className={styles.maintenanceSummary}>{row.summary}</div>
          {row.context && <div className={styles.maintenanceContext}>{row.context}</div>}
        </div>
      ))}
    </div>
  );
}

function ExcludedTab({ components }) {
  const rows = components.flatMap((c) => (c.excluded || []).map((x) => ({ repo: c.repo, ...x })));
  if (!rows.length) return <div className={styles.empty}>Nothing was excluded from this changelog.</div>;
  return (
    <div className={styles.excludedList} data-testid="excluded-tab">
      {rows.map((row) => (
        <div key={`${row.repo}:${row.change_key}`} className={styles.excludedRow}>
          <span className={styles.excludedRepo}>{row.repo.split('/').pop()}</span>
          <a
            className={styles.commitChip}
            href={excludedUrl(row.repo, row.change_key)}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <code className={styles.commitCardSha}>
              {/^\d+$/.test(row.change_key) ? `#${row.change_key}` : row.change_key.slice(0, 7)}
            </code>
            <Ico d={ICO_EXTERNAL} size={11} style={{ color: 'var(--fg-link)', flexShrink: 0 }} />
          </a>
          <span className={styles.excludedReason}>{row.reason}</span>
        </div>
      ))}
    </div>
  );
}

/* apiCall rejects a JSON error as its bare `detail` string (no status), and network
   failures as a TypeError — only the backend's own not-found detail means 404 here. */
export function isNotFound(err) {
  return err?.status === 404 || (typeof err === 'string' && /no (changelog|release notes) stored/i.test(err));
}

/* Why a run has no stored changelog, keyed by its step's operation status. */
const MISSING_MESSAGES = {
  errored: 'No changelog stored for this run: generation failed. Open the workflow run above for the error, then Rerun.',
  cancelled: 'No changelog stored for this run: the step was cancelled.',
  pending: 'The changelog is still being generated. It appears here when the workflow finishes.',
  running: 'The changelog is still being generated. It appears here when the workflow finishes.',
};
const DEFAULT_MISSING_MESSAGE = 'No changelog stored for this run — the step may have been skipped or is still running.';

export default function ChangelogPanel({ runId, status }) {
  const [changelog, setChangelog] = useState(null);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [stats, setStats] = useState([]);
  const [tab, setTab] = useState('changelog');
  const [reanalyze, setReanalyze] = useState(null); // null | 'pending' | 'started' | 'failed'

  useEffect(() => {
    let cancelled = false;
    setChangelog(null);
    setMissing(false);
    setLoadError(false);
    apiGetNightlyChangelog(runId)
      .then((data) => { if (!cancelled) setChangelog(data); })
      .catch((err) => { if (!cancelled) (isNotFound(err) ? setMissing(true) : setLoadError(true)); });
    return () => { cancelled = true; };
  }, [runId]);

  useEffect(() => {
    if (changelog === null) return undefined;
    let cancelled = false;
    apiGetBranchChangelogStats(changelog.branch_version)
      .then((data) => { if (!cancelled) setStats(data.stats || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [changelog]);

  if (loadError) {
    return (
      <div className={styles.sectionGroup} data-testid="changelog-panel">
        <div className={styles.empty}>Couldn't load the changelog — check the releases API and retry.</div>
      </div>
    );
  }
  if (missing) {
    return (
      <div className={styles.sectionGroup} data-testid="changelog-panel">
        <div className={styles.empty}>{MISSING_MESSAGES[status] || DEFAULT_MISSING_MESSAGE}</div>
      </div>
    );
  }
  if (changelog === null) return null;

  const excludedCount = (changelog.components || [])
    .reduce((n, c) => n + ((c.excluded || []).length), 0);
  const maintenanceCount = (changelog.components || [])
    .reduce((n, c) => n + ((c.maintenance || []).length), 0);

  return (
    <div className={styles.sectionGroup} data-testid="changelog-panel">
      <div className={styles.changelogTabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'changelog'}
          className={tab === 'changelog' ? styles.changelogTabActive : styles.changelogTab}
          onClick={() => setTab('changelog')}
        >
          Changelog
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'maintenance'}
          className={tab === 'maintenance' ? styles.changelogTabActive : styles.changelogTab}
          onClick={() => setTab('maintenance')}
        >
          Maintenance ({maintenanceCount})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'excluded'}
          className={tab === 'excluded' ? styles.changelogTabActive : styles.changelogTab}
          onClick={() => setTab('excluded')}
        >
          Excluded ({excludedCount})
        </button>
        <button
          type="button"
          className={styles.reanalyzeButton}
          disabled={reanalyze === 'pending' || reanalyze === 'started'}
          title="Regenerate this changelog with a fresh analysis of every change (stored results for this range are overwritten). Use after a pipeline or prompt fix — for a plain retry of a failed step, use the step's re-run instead."
          onClick={() => {
            setReanalyze('pending');
            apiReanalyzeChangelog(runId)
              .then(() => setReanalyze('started'))
              .catch(() => setReanalyze('failed'));
          }}
        >
          {reanalyze === 'started' ? 'Regenerating…' : reanalyze === 'pending' ? 'Starting…' : 'Regenerate'}
        </button>
      </div>
      {reanalyze === 'started' && (
        <div className={styles.changelogStats}>
          Regenerating with fresh analysis — the changelog updates when the workflow finishes (a few minutes).
        </div>
      )}
      {reanalyze === 'failed' && (
        <div className={styles.changelogStats}>
          Could not start regeneration — check that this run has a stored changelog and try again.
        </div>
      )}
      {tab === 'changelog' && (
        <>
          <div className={styles.changelogStats}>{statsLine(changelog)}</div>
          <GroundedTrend stats={stats} runId={runId} />
          <div className={styles.changelogBody}>
            <ReactMarkdown components={{ a: MarkdownLink }}>
              {changelog.markdown}
            </ReactMarkdown>
          </div>
        </>
      )}
      {tab === 'maintenance' && <MaintenanceTab components={changelog.components || []} />}
      {tab === 'excluded' && <ExcludedTab components={changelog.components || []} />}
    </div>
  );
}
